import React, { useState, useEffect } from 'react';
import { useAdminAuth } from '../hooks/useAdminAuth';
import { ROUTES } from '../constants';
import './AdminLogin.css';

/**
 * Admin Login Page Component
 * Authentication page for admin dashboard access
 * Only users excluded from analytics (test/dev/prod users and PauluneMoon) can access
 */
const AdminLogin = () => {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { login, isAuthenticated } = useAdminAuth();

  // Redirect to dashboard when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      console.log('[Admin Login] User authenticated, redirecting to dashboard...');
      // Use window.location to navigate since we're outside Routes context
      window.location.href = ROUTES.DASHBOARD;
    }
  }, [isAuthenticated]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    // Verify email has admin access (only test/dev/prod users and PauluneMoon)
    const result = await login(email);
    
    if (!result.success) {
      setError(result.error);
      setIsLoading(false);
    } else {
      // Successful login - useEffect will handle redirect when isAuthenticated becomes true
      console.log('[Admin Login] Login successful, waiting for redirect...');
    }
  };

  const handleDevBypass = async () => {
    // Quick login with dev email (contains "dev" so it has admin access)
    const devEmail = 'dev@wesplit.com';
    setEmail(devEmail);
    setIsLoading(true);
    setError('');
    
    const result = await login(devEmail);
    
    if (!result.success) {
      setError(result.error);
      setIsLoading(false);
    } else {
      // Successful login - useEffect will handle redirect when isAuthenticated becomes true
      console.log('[Admin Login] Dev bypass successful, waiting for redirect...');
    }
  };

  return (
    <div className="admin-login">
      <div className="login-container">
        <div className="login-header">
          <h1>WeSplit Admin</h1>
          <p>Enter your authorized admin email to access the dashboard</p>
        </div>
        
        <form onSubmit={handleSubmit} className="login-form">
          <div className="form-group">
            <label htmlFor="email">Email Address</label>
            <div className="input-group">
              <i className="bi bi-envelope input-icon"></i>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your admin email"
                required
                className="admin-input"
              />
            </div>
          </div>
          
          {error && <div className="error-message">{error}</div>}
          
          <button 
            type="submit" 
            className="admin-btn"
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <div className="loading-spinner"></div>
                Signing In...
              </>
            ) : (
              <>
                <i className="bi bi-box-arrow-in-right" style={{ marginRight: 6 }}></i>
                Sign In
              </>
            )}
          </button>
          
          {/* Dev Bypass Button */}
          <button 
            type="button" 
            className="admin-btn admin-btn-secondary dev-bypass-btn"
            onClick={handleDevBypass}
            title="Development bypass - skip authentication"
          >
            <i className="bi bi-code" style={{ marginRight: 6 }}></i>
            Dev Bypass
          </button>
        </form>
      </div>
    </div>
  );
};

export default AdminLogin;
